import { createContext, useState, useCallback, useMemo, useEffect, type ReactNode } from 'react';
import { useParams } from 'crossroad';
import { DEFAULT_VIEW, type BibleBook, type BibleVersion } from '../utils/bible-utils';

/**
 * Provider component for passage navigation state management
 *
 * Manages the current reading position including:
 * - Book and chapter
 * - Scroll position (verse-based)
 * - Passage ID for passage details
 * - Bible version/translation
 *
 * @example
 * ```tsx
 * import { PassageStateProvider } from '@/lib/context/passage-state-provider';
 *
 * function App() {
 *   return (
 *     <PassageStateProvider>
 *       <YourAppComponents />
 *     </PassageStateProvider>
 *   );
 * }
 * ```
 */
interface PassageStateProviderProps {
    children: ReactNode;
}

export interface ScrollState {
    /** Current book (e.g., "Genesis", "Matthew"), null if not yet initialized */
    book: BibleBook | null;
    /** Current chapter number (1-based), null if not yet initialized */
    chapter: number | null;
    /** Current verse number (1-based), null if not set */
    verse: number | null;
}

export interface PassageContextType {
    /** Current scroll state (book, chapter, and scroll position) */
    scrollState: ScrollState;
    /** Set the scroll state */
    setScrollState: (state: ScrollState) => void;

    /** Current passage ID */
    passageId: string | null;
    setPassageId: (id: string | null) => void;

    /** Current Bible version/translation (e.g., "csb", "esv") */
    version: BibleVersion;
    setVersion: (version: BibleVersion) => void;
    /** Whether to show manuscript annotations */
    showAnnotations: boolean;
    setShowAnnotations: (show: boolean) => void;
    /** Whether the current view has any annotations to show */
    hasAnnotations: boolean;
    setHasAnnotations: (has: boolean) => void;
}

export const PassageStateContext = createContext<PassageContextType | undefined>(undefined);

export function PassageStateProvider({ children }: PassageStateProviderProps) {
    const passageIdFromPassageUrl = useParams('/app/passage/:id')?.id;
    const passageIdFromContextUrl = useParams('/app/passage/:id/context/:contextCategory')?.id;
    const passageIdFromUrl = passageIdFromPassageUrl || passageIdFromContextUrl || null;
    const [scrollState, setScrollStateInternal] = useState<ScrollState>({
        book: null,
        chapter: null,
        verse: null
    });
    const [passageId, setPassageIdState] = useState<string | null>(passageIdFromUrl);
    const [version, setVersionState] = useState<BibleVersion>(DEFAULT_VIEW.version);
    const [showAnnotations, setShowAnnotationsState] = useState(true);
    const [hasAnnotations, setHasAnnotationsState] = useState(false);

    // Update passageId when the URL changes
    useEffect(() => {
        setPassageIdState(passageIdFromUrl);
        // Reset hasAnnotations when passage changes, provider will re-update it
        setHasAnnotationsState(false);
    }, [passageIdFromUrl]);

    const setScrollState = useCallback((state: ScrollState) => {
        setScrollStateInternal(state);
    }, []);

    const setPassageId = useCallback((id: string | null) => {
        setPassageIdState(id);
    }, []);

    const setVersion = useCallback((newVersion: BibleVersion) => {
        setVersionState(newVersion);
    }, []);

    const setShowAnnotations = useCallback((show: boolean) => {
        setShowAnnotationsState(show);
    }, []);

    const setHasAnnotations = useCallback((has: boolean) => {
        setHasAnnotationsState(has);
    }, []);

    const value = useMemo(() => ({
        scrollState,
        setScrollState,
        passageId,
        setPassageId,
        version,
        setVersion,
        showAnnotations,
        setShowAnnotations,
        hasAnnotations,
        setHasAnnotations
    }), [scrollState, setScrollState, passageId, setPassageId, version, setVersion, showAnnotations, setShowAnnotations, hasAnnotations, setHasAnnotations]);

    return (
        <PassageStateContext.Provider value={value}>
            {children}
        </PassageStateContext.Provider>
    );
}
